import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import { loadEventOptions } from '../../lib/Api/Event';
import { loadCompanyOptions } from '../../lib/Api/Company';
import { loadPagedParticipants } from '../../lib/Api/Participants';
import { loadEventPosts, deleteEventPost } from '../../lib/Api/Posts';

import {
  PageWrapper,
  NoRecordsFound,
  HeaderNavDropdown,
  Modal,
  ModalButton,
  Checkbox
} from '../../components/UI';
import LoadingIndicator from '../../components/UI/LoadingIndicator';
import Pagination from '../../components/UI/PaginatedTable/Pagination';
import VideoPlayer from '../../components/UI/VideoPlayer';
import AudioPlayer from '../../components/UI/AudioPlayer';

import { eventGuy, trashCan } from '../../assets/icons';
import { DarkBlueHeaderL, RedText } from '../../Themes';

import {
  ApiStatus,
  APIThunkDispatch,
  Company,
  EventPost,
  EventPostsFilter,
  OrderDirection,
  PagedResponse,
  ParticipantFilter,
  ParticipantListView
} from '../../lib/Types';
import { ApplicationState } from '../../lib/Store';
import { POST_CATEGORY_DISPLAY } from '../../CONSTANTS';
import { pfpPlaceholder } from '../../assets/img';

import ParticipantEmailDropdown from './ParticipantEmailDropdown';

import {
  Container,
  EmptyState,
  FilterBar,
  FilterDropdowns,
  FilterSecondRow,
  AdminPostsFilter,
  PostList,
  PostCard,
  PostHeader,
  Avatar,
  AuthorBlock,
  AuthorName,
  PostMeta,
  Badge,
  PostTitle,
  PostMessage,
  PostImage,
  MediaWrapper,
  CompanyTags,
  CompanyTag,
  DeleteButton,
  DeleteIcon,
  PaginationWrapper,
  ButtonWrapper
} from './Styles';

const defaultPostsFilter: EventPostsFilter = {
  page: 1,
  rowsPerPage: 10,
  orderColumn: 'date',
  orderDirection: 'DESC'
};

const participantListFilter = {
  page: 1,
  rowsPerPage: 500,
  orderColumn: 'lastName',
  orderDirection: 'ASC' as OrderDirection,
  keyword: ''
};

const ViewPosts: FC = () => {
  const Dispatch: APIThunkDispatch = useDispatch();

  const eventOptions = useSelector((state: ApplicationState) => state.eventState.eventOptions);

  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [adminPostsOnly, setAdminPostsOnly] = useState(false);
  const [companyOptions, setCompanyOptions] = useState<Company[]>([]);
  const [participantOptions, setParticipantOptions] = useState<ParticipantListView[]>([]);
  const [filter, setFilter] = useState<EventPostsFilter>(defaultPostsFilter);
  const [posts, setPosts] = useState<PagedResponse<EventPost>>({ results: [], count: 0 });
  const [loading, setLoading] = useState(false);
  const [deletingPostId, setDeletingPostId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const activeEvents = useMemo(() => (eventOptions || []).filter(e => e.active), [eventOptions]);

  const postsFilter = useMemo<EventPostsFilter>(() => {
    const next: EventPostsFilter = { ...filter };
    if (adminPostsOnly) {
      next.adminOnly = true;
    }
    if (selectedCompanyId) {
      next.companyId = selectedCompanyId;
      if (!adminPostsOnly && selectedUserId) {
        next.userId = selectedUserId;
      }
    }
    return next;
  }, [filter, adminPostsOnly, selectedCompanyId, selectedUserId]);

  const companyFilterDisabled = !selectedEventId;
  const participantFilterDisabled = !selectedCompanyId || adminPostsOnly;

  useEffect(() => {
    (async () => await Dispatch(loadEventOptions()))();
  }, []);

  useEffect(() => {
    if (!selectedEventId) {
      setCompanyOptions([]);
      return;
    }
    (async () => {
      const { data } = await Dispatch(loadCompanyOptions(selectedEventId));
      if (data) setCompanyOptions(data);
    })();
  }, [selectedEventId, Dispatch]);

  useEffect(() => {
    if (!selectedEventId || !selectedCompanyId || adminPostsOnly) {
      setParticipantOptions([]);
      return;
    }
    (async () => {
      const { data } = await Dispatch(
        loadPagedParticipants(
          {
            ...participantListFilter,
            companyId: selectedCompanyId,
            eventId: selectedEventId
          } as ParticipantFilter,
          selectedEventId
        )
      );
      if (data) setParticipantOptions(data.results);
    })();
  }, [selectedEventId, selectedCompanyId, adminPostsOnly, Dispatch]);

  const fetchPosts = useCallback(async () => {
    if (!selectedEventId) {
      setPosts({ results: [], count: 0 });
      return;
    }
    setLoading(true);
    const { data } = await Dispatch(loadEventPosts(selectedEventId, postsFilter));
    if (data) setPosts(data);
    setLoading(false);
  }, [Dispatch, selectedEventId, postsFilter]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleSelectEvent = (eventId: number | null) => {
    setFilter(defaultPostsFilter);
    setSelectedEventId(eventId);
    setSelectedCompanyId(null);
    setSelectedUserId(null);
    setAdminPostsOnly(false);
  };

  const handleSelectCompany = (companyId: number | null) => {
    setFilter(defaultPostsFilter);
    setSelectedCompanyId(companyId);
    setSelectedUserId(null);
  };

  const handleSelectParticipant = (userId: string | null) => {
    if (adminPostsOnly) return;
    setFilter(prev => ({ ...prev, page: 1 }));
    setSelectedUserId(userId);
  };

  const handleAdminPostsToggle = (checked: boolean) => {
    setFilter(defaultPostsFilter);
    setAdminPostsOnly(checked);
    setSelectedUserId(null);
  };

  const setPage = (page: number) => setFilter(prev => ({ ...prev, page }));

  // Show only root, non-deleted posts (endpoint also returns replies + soft-deleted rows).
  const visiblePosts = useMemo(
    () => posts.results.filter(post => !post.deleted && post.parentPostId === null),
    [posts.results]
  );

  const handleConfirmDelete = async () => {
    if (!deletingPostId) return;
    setIsDeleting(true);
    const { status } = await Dispatch(deleteEventPost(deletingPostId));
    setIsDeleting(false);
    setDeletingPostId(null);
    if (status === ApiStatus.SUCCESS) await fetchPosts();
  };

  const renderPost = (post: EventPost) => {
    const fullName = `${post.user?.firstName || ''} ${post.user?.lastName || ''}`.trim();
    const categoryLabel = POST_CATEGORY_DISPLAY[post.category] || post.category;
    return (
      <PostCard key={post.postId}>
        <DeleteButton title="Delete post" onClick={() => setDeletingPostId(post.postId)}>
          <DeleteIcon src={trashCan} alt="Delete" />
        </DeleteButton>
        <PostHeader>
          <Avatar
            src={post.user?.profileImage || pfpPlaceholder}
            alt={fullName || 'Profile'}
            onError={e => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = pfpPlaceholder;
            }}
          />
          <AuthorBlock>
            <AuthorName>{fullName || post.user?.email || 'Unknown user'}</AuthorName>
            <PostMeta>{moment(Number(post.date)).format('DD MMM YYYY, h:mm a')}</PostMeta>
          </AuthorBlock>
        </PostHeader>
        <Badge>
          {post.type}
          {categoryLabel ? ` · ${categoryLabel}` : ''}
        </Badge>
        {post.title ? <PostTitle>{post.title}</PostTitle> : null}
        {post.message ? <PostMessage>{post.message}</PostMessage> : null}
        {post.image ? <PostImage src={post.image} alt={post.title || 'Post image'} /> : null}
        {post.video ? (
          <MediaWrapper>
            <VideoPlayer video={post.video} />
          </MediaWrapper>
        ) : null}
        {post.audio ? (
          <MediaWrapper>
            <AudioPlayer audioURI={post.audio} />
          </MediaWrapper>
        ) : null}
        {post.companies?.length ? (
          <CompanyTags>
            {post.companies.map(company => (
              <CompanyTag key={company.id}>{company.name}</CompanyTag>
            ))}
          </CompanyTags>
        ) : null}
      </PostCard>
    );
  };

  const renderContent = () => {
    if (!selectedEventId) {
      return <EmptyState>Please select an event above</EmptyState>;
    }
    if (loading) {
      return <LoadingIndicator />;
    }
    if (!visiblePosts.length) {
      const message = adminPostsOnly
        ? 'There are no administrator posts for this event yet.'
        : 'There are no posts yet.';
      return <NoRecordsFound icon={eventGuy} message={message} />;
    }
    return (
      <>
        <PostList>{visiblePosts.map(renderPost)}</PostList>
        <PaginationWrapper>
          <Pagination
            page={filter.page}
            count={posts.count}
            rowsPerPage={filter.rowsPerPage}
            loading={loading}
            pageInput={false}
            onPrevPage={() => setPage(filter.page - 1)}
            onNextPage={() => setPage(filter.page + 1)}
            onSetPage={setPage}
          />
        </PaginationWrapper>
      </>
    );
  };

  const filterDropdowns = (
    <FilterBar>
      <FilterDropdowns>
        <HeaderNavDropdown
          data={activeEvents}
          dataType="event"
          defaultLabel="Select an event"
          listLoadFunction={() => null}
          onOptionClick={(_dataType, { id }: { id: string }) =>
            handleSelectEvent(id === 'all' ? null : +id)
          }
        />
        <HeaderNavDropdown
          key={`company-${selectedEventId ?? 'none'}`}
          data={companyOptions}
          dataType="company"
          disabled={companyFilterDisabled}
          defaultLabel="Select an organisation"
          listLoadFunction={() => null}
          onOptionClick={(_dataType, { id }: { id: string }) =>
            handleSelectCompany(id === 'all' ? null : +id)
          }
        />
      </FilterDropdowns>
      <FilterSecondRow>
        <AdminPostsFilter $disabled={!selectedEventId}>
          <Checkbox
            checked={adminPostsOnly}
            disabled={!selectedEventId}
            label="Admin posts only"
            boldLabel
            onChange={e => handleAdminPostsToggle(e.target.checked)}
          />
        </AdminPostsFilter>
        <ParticipantEmailDropdown
          participants={participantOptions}
          value={selectedUserId}
          disabled={participantFilterDisabled}
          placeholder="Select a participant"
          onChange={handleSelectParticipant}
        />
      </FilterSecondRow>
    </FilterBar>
  );

  return (
    <PageWrapper
      nav={false}
      event={false}
      company={false}
      wideHeaderFilters
      formOpener={filterDropdowns}
    >
      <Container>{renderContent()}</Container>

      {deletingPostId ? (
        <Modal
          top={5}
          left={30}
          width={40}
          icon={eventGuy}
          padding={'20px 40px'}
          title={'Delete this post?'}
        >
          <DarkBlueHeaderL>
            <RedText>This will remove the post from the event feed.</RedText>
          </DarkBlueHeaderL>
          <br />
          <ButtonWrapper>
            <ModalButton
              type="button"
              variant={'remove'}
              disabled={isDeleting}
              onClick={handleConfirmDelete}
            >
              {isDeleting ? 'Deleting...' : 'Yes, Delete'}
            </ModalButton>
            <ModalButton
              type="button"
              variant={'primary'}
              disabled={isDeleting}
              onClick={() => setDeletingPostId(null)}
            >
              No, Keep
            </ModalButton>
          </ButtonWrapper>
        </Modal>
      ) : null}
    </PageWrapper>
  );
};

export default ViewPosts;
